import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Lock,
  Key,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";

export default function GantiPassword() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // Form State
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [loading, setLoading] = useState(false);

  // Toast State
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });

  useEffect(() => {
    // Ambil data user yang sedang login dari LocalStorage
    const savedUser = JSON.parse(localStorage.getItem("ruangKelasUser"));
    if (!savedUser) {
      navigate("/"); // Jika belum login, tendang ke home
    } else {
      setUser(savedUser);
    }
  }, [navigate]);

  const showToast = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validasi Input
    if (!oldPass || !newPass || !confirmPass) {
      return showToast("Semua kolom wajib diisi!", "error");
    }
    if (newPass !== confirmPass) {
      return showToast("Konfirmasi password baru tidak cocok!", "error");
    }
    if (newPass.length < 3) {
      return showToast("Password baru terlalu pendek!", "error");
    }

    setLoading(true);

    try {
      // 2. Verifikasi Password Lama ke Database
      // Kita cari akun dengan username ini DAN password lama ini
      const { data: akunValid, error: errCek } = await supabase
        .from("akun")
        .select("id")
        .eq("username", user.username)
        .eq("password", oldPass) // Cek password lama (Plain text utk simulasi)
        .single();

      if (errCek || !akunValid) {
        showToast("Password Lama Anda Salah!", "error");
        setLoading(false);
        return;
      }

      // 3. Update Password Baru
      const { error: errUpdate } = await supabase
        .from("akun")
        .update({ password: newPass })
        .eq("id", akunValid.id);

      if (errUpdate) throw errUpdate;

      // 4. Sukses
      showToast("Password berhasil diubah!", "success");
      setOldPass("");
      setNewPass("");
      setConfirmPass(""); // Reset form
    } catch (error) {
      showToast("Gagal update: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const styles = {
    container: {
      padding: "20px",
      maxWidth: "600px",
      margin: "0 auto",
      fontFamily: "'Inter', sans-serif",
    },
    card: {
      background: "white",
      padding: "30px",
      borderRadius: "20px",
      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
      border: "1px solid #F1F5F9",
    },
    inputLabel: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "600",
      fontSize: "13px",
      color: "#64748B",
      textTransform: "uppercase",
    },
    input: {
      width: "100%",
      padding: "14px",
      borderRadius: "10px",
      border: "1px solid #CBD5E1",
      marginBottom: "20px",
      boxSizing: "border-box",
      outline: "none",
      fontSize: "15px",
      background: "#F8FAFC",
    },
    btnSave: {
      width: "100%",
      background: "linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)",
      color: "white",
      padding: "14px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      fontWeight: "bold",
      fontSize: "15px",
    },

    // Toast
    toastContainer: {
      position: "fixed",
      top: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 20px",
      borderRadius: "12px",
      background: "white",
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
      border: "1px solid #E2E8F0",
      minWidth: "300px",
      animation: "slideDown 0.3s ease-out",
    },
  };

  return (
    <div style={styles.container}>
      {/* Toast */}
      {toast.show && (
        <div
          style={{
            ...styles.toastContainer,
            borderLeft:
              toast.type === "success"
                ? "4px solid #10B981"
                : "4px solid #EF4444",
          }}
        >
          {toast.type === "success" ? (
            <CheckCircle size={20} color="#059669" />
          ) : (
            <AlertCircle size={20} color="#DC2626" />
          )}
          <div
            style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#334155" }}
          >
            {toast.msg}
          </div>
          <X
            size={16}
            style={{ cursor: "pointer" }}
            onClick={() => setToast({ ...toast, show: false })}
          />
        </div>
      )}

      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          textDecoration: "none",
          color: "#64748B",
          marginBottom: "20px",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <ArrowLeft size={18} /> Kembali ke Dashboard
      </Link>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 15,
          marginBottom: 25,
        }}
      >
        <div style={{ background: "#EEF2FF", padding: 12, borderRadius: 14 }}>
          <Key size={32} color="#4F46E5" />
        </div>
        <div>
          <h2 style={{ margin: 0, color: "#1E293B", fontSize: 24 }}>
            Ganti Password
          </h2>
          <p style={{ margin: 0, color: "#64748B", fontSize: 14 }}>
            Amankan akun Anda secara berkala
          </p>
        </div>
      </div>

      <div style={styles.card}>
        <form onSubmit={handleSubmit}>
          <label style={styles.inputLabel}>Password Lama</label>
          <div style={{ position: "relative" }}>
            <input
              type="password"
              style={styles.input}
              placeholder="Masukkan password saat ini"
              value={oldPass}
              onChange={(e) => setOldPass(e.target.value)}
            />
            <Lock
              size={18}
              color="#94A3B8"
              style={{ position: "absolute", right: 15, top: 15 }}
            />
          </div>

          <hr
            style={{
              border: "none",
              borderTop: "1px dashed #E2E8F0",
              margin: "0 0 20px 0",
            }}
          />

          <label style={styles.inputLabel}>Password Baru</label>
          <input
            type="password"
            style={styles.input}
            placeholder="Minimal 3 karakter"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
          />

          <label style={styles.inputLabel}>Ulangi Password Baru</label>
          <input
            type="password"
            style={styles.input}
            placeholder="Ketik ulang password baru"
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
          />

          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            style={{ ...styles.btnSave, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? (
              "Memproses..."
            ) : (
              <>
                <Save size={18} /> Simpan Password Baru
              </>
            )}
          </button>
        </form>
      </div>

      <style>{`@keyframes slideDown { from {transform: translate(-50%, -100%); opacity:0;} to {transform: translate(-50%, 0); opacity:1;} }`}</style>
    </div>
  );
}
